<script setup lang="ts">
import { useTemplateRef } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { useStaticAsset } from '@/composables/useStaticAsset'
import type { DashboardContext } from './types'

import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import AceProlongModal, { type AceProlongModalExpose } from '@/ui-kit/components/AceProlongModal/AceProlongModal.vue'
import Onboarding from './Onboarding.vue'
import { currentUser, licensingUserId } from '~/lib/currentUser'
import EducationList from './EducationList.vue'
import JournalGroups from './JournalGroups.vue'
import StudentCabinetModal, { type CabinetSelectModalExpose } from './StudentCabinetModal.vue'

const asset = useStaticAsset()
const ctx = usePageContext<DashboardContext>()
const { isMobile } = useScreen()

const profileUrl = '/teacher/bio/my'
const aceProlongModal = useTemplateRef<AceProlongModalExpose>('aceProlongModal')
const studentCabinetModal = useTemplateRef<CabinetSelectModalExpose>('studentCabinetModal')
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="teacher" :rules-url="ctx.rules_url" />

    <div class="content teacher-dashboard">
      <v-notifications position="bottom right" />
      <div v-if="currentUser.id === licensingUserId" class="header mb-4">Рабочий стол преподавателя</div>
      <div v-else class="header mb-4">Рабочий стол</div>

      <div class="teacher-dashboard__grid">
        <aside class="d-flex flex-column teacher-dashboard__grid-aside">
          <div class="teacher-dashboard__link-group-card">
            <v-button mode="primary filled x-large block" :href="profileUrl">
              <div class="d-flex align-center py-1 text-title-bold">
                <img :src="ctx.profile_image_url" class="teacher-dashboard__btn-avatar mr-2" />
                Моя визитка
              </div>
            </v-button>

            <v-button class="mt-2" mode="warn filled x-large block" href="/journal/schedule">
              <div class="d-flex align-center py-1 text-title-bold">
                <v-icon class="mr-2" size="28">mdi-book-open-blank-variant</v-icon>
                Журнал
              </div>
            </v-button>

            <v-link mode="primary" href="/teacher/route" class="mt-2 text-title-bold"> Маршрут роста </v-link>
            <v-link mode="primary" href="/seminars/my_education" class="mt-2 text-title-bold"> Моё обучение </v-link>
          </div>

          <v-link v-if="isMobile" mode="primary" href="/website/news/1" class="mt-2 text-title-bold">
            Наши новости
            <img :src="asset('img/method-logo.webp')" style="width: 24px; height: 24px; margin-left: 8px" />
          </v-link>

          <div class="my-2" />

          <div class="teacher-dashboard__card teacher-dashboard__card_blue">
            <v-icon class="teacher-dashboard__card-icon" size="32">student-cap</v-icon>
            <h2 class="teacher-dashboard__card-title">Онлайн-преподавание</h2>
            <div class="teacher-dashboard__card-links">
              <div class="teacher-dashboard__link-badge" :data-badge-num="ctx.incoming_online_teaching_requests_count">
                <v-link mode="primary" class="mr-2 mb-half" href="/online_teaching/incoming_trial_lesson_requests">
                  Мои заявки
                </v-link>
              </div>
              <v-link mode="primary" class="mr-2 mb-half" href="/online_teaching/trial_lesson_offer">
                Моя анкета
              </v-link>
              <v-link mode="primary" class="mr-2" href="/teacher/bulk_smartbook_manage"> Оптовая закупка УУ </v-link>
            </div>
          </div>

          <div v-if="ctx.is_partner" class="teacher-dashboard__card teacher-dashboard__card_green">
            <v-icon class="teacher-dashboard__card-icon" size="32">partner</v-icon>
            <h2 class="teacher-dashboard__card-title">Партнёрская программа</h2>
            <div v-if="ctx.partner_eula_accepted" class="teacher-dashboard__card-links">
              <v-link mode="primary" class="mr-2 mb-half" href="/partners/products">Ссылки и реклама</v-link>
              <v-link mode="primary" class="mr-2" href="/partners/stats">Статистика</v-link>
            </div>
            <div v-else class="teacher-dashboard__card-links">
              <v-link mode="primary" class="mr-2 mb-half" href="/partners/eula">Ознакомиться с правилами</v-link>
            </div>
          </div>

          <div v-if="ctx.is_tutor" class="teacher-dashboard__card teacher-dashboard__card_orange">
            <v-icon class="teacher-dashboard__card-icon" size="32">cert-tutor-done</v-icon>
            <h2 class="teacher-dashboard__card-title">Кабинет тьютора</h2>
            <div class="teacher-dashboard__card-links">
              <div class="teacher-dashboard__link-badge" :data-badge-num="ctx.incoming_tutor_requests_count">
                <v-link mode="primary" class="mr-2" href="/tutors/tutor_cabinet">Мои заявки</v-link>
              </div>
              <v-link mode="primary" class="mr-2" href="/tutors/edit_offer">Моя анкета</v-link>
            </div>
          </div>

          <div v-if="ctx.methodist_links.length" class="teacher-dashboard__card teacher-dashboard__card_red">
            <v-icon class="teacher-dashboard__card-icon" size="32">crown</v-icon>
            <h2 class="teacher-dashboard__card-title">Кабинет методиста</h2>
            <div class="teacher-dashboard__card-links" style="flex-direction: column">
              <v-link
                v-for="(link, i) in ctx.methodist_links"
                :key="i"
                mode="primary"
                class="mr-2 mb-half"
                :href="link.url"
              >
                &bullet; {{ link.text }}
              </v-link>
            </div>
          </div>

          <div v-if="ctx.has_active_aces" class="teacher-dashboard__card teacher-dashboard__card_red">
            <v-icon class="teacher-dashboard__card-icon" size="32">certificate</v-icon>
            <h2 class="teacher-dashboard__card-title">Сертификаты АС</h2>
            <div class="teacher-dashboard__card-links">
              <v-link mode="primary" @click="aceProlongModal?.$open()">Продление сертификатов</v-link>
            </div>
          </div>
        </aside>

        <Onboarding
          v-if="!currentUser.hasTeacherPurchase"
          class="teacher-dashboard__grid-content"
          :entrypoints="ctx.entry_points"
        />

        <ul v-else class="d-flex flex-column teacher-dashboard__grid-content">
          <EducationList :education-data="ctx.my_education" />
          <JournalGroups
            v-if="ctx.jgroups.length"
            :groups="ctx.jgroups"
            @open-student-cabinet="groupId => studentCabinetModal?.$open(groupId)"
          />
        </ul>
      </div>

      <AceProlongModal ref="aceProlongModal" />
      <StudentCabinetModal ref="studentCabinetModal" :groups="ctx.jgroups" />
      <v-footer />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/teacher/dashboard.scss' as *;
</style>
